import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { ArrowLeft, Clock, Sparkles, GraduationCap, PawPrint } from "lucide-react";
import { getGuide, getBreeds } from "@/lib/api";
import { usePageMeta } from "@/lib/usePageMeta";

// Which breeds a given training guide is most relevant to, mirroring the
// recommendation logic used on breed profiles. `null` = applies to every breed.
const GUIDE_BREED_MATCH = {
  "how-dogs-learn": null,
  "reliable-recall": (b) => b.energy >= 4,
  "loose-lead-walking": (b) => b.energy >= 4,
  "separation-anxiety": (b) => b.hours_alone_ok <= 2,
  "lead-reactivity": (b) => b.barking >= 4 || b.novice_ok <= 2,
  "resource-guarding": (b) => b.novice_ok <= 2 || b.good_with_pets <= 2,
};

// Note: list keys in this file use array indexes intentionally — guide content
// is static, ordered text that never reorders, so index keys are stable here.
function renderMarkdownish(text) {
  return text.split("\n").map((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return <div key={i} className="h-3" />;
    if (trimmed.startsWith("### ")) {
      return (
        <h3 key={i} className="font-display font-bold text-2xl text-accent mt-8 mb-2">
          {trimmed.slice(4)}
        </h3>
      );
    }
    // Turn [text](url) into <a> and **bold** into <strong>
    const html = trimmed
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary underline hover:no-underline">$1</a>');
    return (
      <p
        key={i}
        className="text-lg leading-relaxed text-accent/90"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html, { ALLOWED_TAGS: ["strong", "a"], ALLOWED_ATTR: ["href", "class"] }) }}
      />
    );
  });
}

function ComparisonTable({ table }) {
  const dataColCount = table.rows[0]?.cells?.length || 0;
  const lastIdx = dataColCount - 1;
  return (
    <div className="mt-8 -mx-6 md:mx-0 md:rounded-3xl overflow-hidden border border-border/60 warm-shadow">
      {/* Desktop: real table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-secondary text-accent">
              {table.columns.map((c, i) => (
                <th
                  key={i}
                  className={`text-left font-display font-bold text-base px-5 py-4 ${
                    i === 0 ? "w-[16%]" : ""
                  } ${i === table.columns.length - 1 ? "bg-accent text-accent-foreground" : ""}`}
                >
                  {c || "\u00A0"}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, ri) => (
              <tr
                key={ri}
                className={`border-t border-border/60 ${ri % 2 === 1 ? "bg-muted/40" : "bg-card"}`}
              >
                <td className="px-5 py-4 align-top font-display font-bold text-accent">
                  {row.label}
                </td>
                {row.cells.map((cell, ci) => {
                  const isLast = ci === lastIdx;
                  return (
                    <td
                      key={ci}
                      className={`px-5 py-4 align-top leading-relaxed ${
                        isLast
                          ? "bg-accent/95 text-accent-foreground"
                          : "text-accent/85"
                      }`}
                    >
                      {isLast && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-primary-foreground bg-primary rounded-full px-2 py-0.5 mb-1.5">
                          <Sparkles size={10} /> {table.columns[ci + 1]}
                        </span>
                      )}
                      <div>{cell}</div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked cards */}
      <div className="md:hidden">
        {table.rows.map((row, ri) => (
          <div key={ri} className="border-b border-border/60 last:border-b-0 p-6 bg-card">
            <h4 className="font-display font-bold text-lg text-accent mb-3">{row.label}</h4>
            {row.cells.map((cell, ci) => {
              const isLast = ci === lastIdx;
              return (
                <div key={ci} className="mt-3">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                    {table.columns[ci + 1]}
                  </div>
                  <div className={`text-sm mt-1 leading-relaxed ${isLast ? "text-accent font-medium" : "text-accent/80"}`}>
                    {cell}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function GuideDetail() {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [breeds, setBreeds] = useState([]);
  usePageMeta(
    guide && `${guide.title} | Fetchright Care Guides`,
    guide && (guide.excerpt || '').slice(0, 155)
  );
  useEffect(() => { getGuide(slug).then(setGuide); }, [slug]);
  useEffect(() => {
    if (slug in GUIDE_BREED_MATCH && GUIDE_BREED_MATCH[slug]) {
      getBreeds({ limit: 200 }).then(setBreeds).catch(() => setBreeds([]));
    }
  }, [slug]);

  if (!guide) return <div className="p-10 text-muted-foreground">Loading…</div>;

  const isTraining = guide.category === "Training & Behaviour";
  const matcher = GUIDE_BREED_MATCH[slug];
  const appliesToAll = isTraining && slug in GUIDE_BREED_MATCH && matcher === null;
  const matchedBreeds = matcher ? breeds.filter(matcher).slice(0, 10) : [];

  const isWide = !!guide.table;
  const wrapCls = isWide
    ? "max-w-6xl mx-auto px-6 lg:px-10 pt-10 pb-24"
    : "max-w-3xl mx-auto px-6 lg:px-10 pt-10 pb-24";

  return (
    <article className={wrapCls}>
      <Link to="/guides" data-testid="guide-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-accent">
        <ArrowLeft size={14} /> All guides
      </Link>
      <h1 data-testid="guide-title" className="mt-6 font-display font-bold text-4xl md:text-5xl text-accent leading-tight">
        {guide.title}
      </h1>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Clock size={14} /> {guide.read_min} min read
        </span>
        {guide.category === "Training & Behaviour" && (
          <span data-testid="guide-byline" className="inline-flex items-center gap-1.5 text-accent">
            <GraduationCap size={14} className="text-primary" />
            Written by <strong className="font-semibold">Fetchright&apos;s Qualified Trainer &amp; Behaviourist</strong>
          </span>
        )}
      </div>
      <div className="mt-10 space-y-4">
        {renderMarkdownish(guide.content)}
      </div>
      {guide.table && <ComparisonTable table={guide.table} />}
      {guide.outro && (
        <div className="mt-10 space-y-4">{renderMarkdownish(guide.outro)}</div>
      )}

      {isTraining && (appliesToAll || matchedBreeds.length > 0) && (
        <div className="mt-12 rounded-3xl border border-border/60 bg-secondary/40 p-6 md:p-8" data-testid="guide-breeds-applies">
          <div className="flex items-center gap-2.5 mb-3">
            <PawPrint size={18} className="text-primary" />
            <h3 className="font-display font-bold text-xl text-accent">
              {appliesToAll ? "Applies to every breed" : "Breeds this especially applies to"}
            </h3>
          </div>
          {appliesToAll ? (
            <p className="text-accent/80">
              Understanding how dogs learn is the foundation for training <strong>any</strong> dog — every breed benefits from this one.{" "}
              <Link to="/breeds" className="text-primary underline hover:no-underline">Browse all breeds →</Link>
            </p>
          ) : (
            <>
              <p className="text-accent/80 mb-4 text-sm">
                Based on temperament and energy, this guide is particularly useful for owners of:
              </p>
              <div className="flex flex-wrap gap-2">
                {matchedBreeds.map((b) => (
                  <Link
                    key={b.id}
                    to={`/breeds/${b.id}`}
                    data-testid={`guide-applies-breed-${b.id}`}
                    className="pill-btn inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card px-4 py-2 text-sm font-medium text-accent hover:border-primary/60"
                  >
                    {b.name}
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </article>
  );
}
